import { dashboardPaint, dashboardCss, dashboardActionForeground } from '../utils/dashboardTheme';
/* ────────────────────────────────────────────────────────── */
/*  src/components/AdminDashboard.jsx                        */
/*  Enhanced with Question History tab and ItemAnalysis      */
/* ────────────────────────────────────────────────────────── */

import React, { Suspense, lazy, useState, useEffect, useRef } from 'react';
import {
  Menu,
  X,
  RefreshCw,
  LogOut,
  User,
  Users,
  Settings,
  FileText,
  BarChart2,
  FileBox,
  Clock,
  Shield,
  AlertTriangle,
  Upload,
  BarChart3,
  Database, // Added for Question History icon
  Target, // Added for Item Analysis icon
  Activity,
} from 'lucide-react';

import AssignUser           from './AssignUser';
import { api } from '../api/client';
import { useAdmin } from '../contexts/AdminContext';
import AppFooter from './AppFooter';
import DashboardThemeProvider from '../contexts/DashboardThemeContext';
import DashboardThemeControl from './DashboardThemeControl';

// Dashboard sections are loaded only when needed. This keeps chart/PDF tooling
// out of the initial admin bundle and makes the Assign screen interactive sooner.
const dashboardLoaders = Object.freeze({
  userList: () => import('./UserList'),
  subUsers: () => import('./SubUserList'),
  adminSettings: () => import('./AssessmentSettings'),
  teacherSettings: () => import('./TeacherAssessmentSettings'),
  performance: () => import('./StudentPerformance'),
  assessments: () => import('./AssessmentManager'),
  createAssessment: () => import('./TeacherAssessmentManager'),
  responses: () => import('./StudentResponses'),
  reports: () => import('./Reports'),
  sessions: () => import('./SessionManagement'),
  questionHistory: () => import('./QuestionHistoryAdmin'),
  itemAnalysis: () => import('./ItemAnalysis'),
  itemBank: () => import('./ItemBank'),
  lessonMonitor: () => import('./LessonMonitor'),
  operationalHealth: () => import('./OperationalHealth')
});

const UserList = lazy(dashboardLoaders.userList);
const SubUserList = lazy(dashboardLoaders.subUsers);
const AssessmentSettings = lazy(dashboardLoaders.adminSettings);
const TeacherAssessmentSettings = lazy(dashboardLoaders.teacherSettings);
const StudentPerformance = lazy(dashboardLoaders.performance);
const AssessmentManager = lazy(dashboardLoaders.assessments);
const TeacherAssessmentManager = lazy(dashboardLoaders.createAssessment);
const StudentResponses = lazy(dashboardLoaders.responses);
const Reports = lazy(dashboardLoaders.reports);
const SessionManagement = lazy(dashboardLoaders.sessions);
const LessonMonitor = lazy(dashboardLoaders.lessonMonitor);
const QuestionHistoryAdmin = lazy(dashboardLoaders.questionHistory);
const ItemAnalysis = lazy(dashboardLoaders.itemAnalysis);
const ItemBank = lazy(dashboardLoaders.itemBank);
const OperationalHealth = lazy(dashboardLoaders.operationalHealth);

function preloadDashboardTab(tabKey, isTeacher) {
  const loaderKey = tabKey === 'settings'
    ? (isTeacher ? 'teacherSettings' : 'adminSettings')
    : tabKey;
  dashboardLoaders[loaderKey]?.();
}

function DashboardContent({ onLogout }) {
  /* ───────── context ───────── */
  const {
    currentUser,
    logoutUser,
    refreshUsers,
    forceRefreshUserSettings,
    assignedUsers,
    assessmentSettings,
    getPerformanceStats,
    refreshAssessments,
    refreshStudentResponses,
    refreshSessions,
  } = useAdmin();

  /* ───────── local state ───────── */
  const [isRefreshing,      setIsRefreshing]      = useState(false);
  const [refreshError,      setRefreshError]      = useState(null);
  const [isMobileMenuOpen,  setIsMobileMenuOpen]  = useState(false);
  const [isMobileView,      setIsMobileView]      = useState(window.innerWidth < 768);
  const [isTabletView,      setIsTabletView]      = useState(window.innerWidth >= 768 && window.innerWidth < 1024);
  const [subUsers,          setSubUsers]          = useState([]);
  const [loadingSubUsers,   setLoadingSubUsers]   = useState(false);
  const logoutInFlight = useRef(false);
  const desktopTabRefs = useRef([]);
  const isTeacher = currentUser?.role === 'teacher' || currentUser?.role === 'main_user';

  // Load subusers for teacher to determine if Create Assessment tab should be shown
  useEffect(() => {
    const fetchSubUsers = async () => {
      if (isTeacher && currentUser?.id) {
        setLoadingSubUsers(true);
        try {
          const fetchedSubUsers = await api.getSubUsers(currentUser.id);
          setSubUsers(fetchedSubUsers || []);
        } catch (err) {
          console.error('Error fetching subusers:', err);
          setSubUsers([]);
        } finally {
          setLoadingSubUsers(false);
        }
      }
    };
    
    fetchSubUsers();
  }, [isTeacher, currentUser?.id]);

  /* tabs with shorter labels for better fitting - updated with ItemAnalysis */
  const adminTabs = [
    { key: 'assign',          label: 'Assign',        shortLabel: 'Assign',     icon: <User size={16} /> },
    { key: 'userList',        label: 'Users',         shortLabel: 'Users',      icon: <Users size={16} /> },
    { key: 'settings',        label: 'Settings',      shortLabel: 'Settings',   icon: <Settings size={16} /> },
    { key: 'assessments',     label: 'Assessments',   shortLabel: 'Assess.',    icon: <FileText size={16} /> },
    { key: 'performance',     label: 'Performance',   shortLabel: 'Perform.',   icon: <BarChart2 size={16} /> },
    { key: 'responses',       label: 'Responses',     shortLabel: 'Responses',  icon: <FileBox size={16} /> },
    { key: 'sessions',        label: 'Sessions',      shortLabel: 'Sessions',   icon: <Clock size={16} /> },
    { key: 'reports',         label: 'Reports',       shortLabel: 'Reports',    icon: <BarChart3 size={16} /> },
    { key: 'lessonMonitor', label:'Lesson Monitor',shortLabel:'Lessons',icon:<Activity size={16}/> },
    { key: 'questionHistory', label: 'Q. History',    shortLabel: 'Q. Hist.',   icon: <Database size={16} /> },
    { key: 'itemAnalysis',    label: 'Item Analysis', shortLabel: 'Item Ana.',  icon: <Target size={16} /> }, // Added Item Analysis tab
    { key: 'itemBank',        label: 'Item Bank',     shortLabel: 'Bank',       icon: <Database size={16} /> },
    { key: 'operationalHealth', label: 'Health',      shortLabel: 'Health',     icon: <Activity size={16} /> },
  ];

  // Generate teacher tabs based on whether there are subusers
  const getTeacherTabs = () => {
    const baseTabs = [
        { key: 'performance', label: 'Performance',   shortLabel: 'Perform.',   icon: <BarChart2 size={16} /> },
        { key: 'settings',    label: 'Settings',      shortLabel: 'Settings',   icon: <Settings  size={16} /> },
        { key: 'subUsers',    label: 'SubUsers',      shortLabel: 'SubUsers',   icon: <Users size={16} /> },
        { key: 'lessonMonitor',label:'Lesson Monitor',shortLabel:'Lessons',icon:<Activity size={16}/> },
        { key: 'sessions',    label: 'Sessions',      shortLabel: 'Sessions',   icon: <Clock size={16} /> },
      ];

      // Add tabs only if there are subusers
      if (subUsers.length > 0) {
        baseTabs.push({ key: 'reports', label: 'Reports', shortLabel: 'Reports', icon: <BarChart3 size={16} /> });
        baseTabs.push({ key: 'createAssessment', label: 'Create', shortLabel: 'Create', icon: <Upload size={16} /> });
      }

      return baseTabs;
  };

  const teacherTabs = getTeacherTabs();
  const tabsToShow = isTeacher ? teacherTabs : adminTabs;
  const [activeTab, setActiveTab] = useState(isTeacher ? 'performance' : 'assign');
  const [monitorLearner,setMonitorLearner]=useState(null);
  useEffect(()=>{const open=event=>{setMonitorLearner(event.detail?.learnerId||null);setActiveTab('lessonMonitor');};window.addEventListener('assessnow-open-lesson-monitor',open);return()=>window.removeEventListener('assessnow-open-lesson-monitor',open);},[]);

  const menuGroupDefinitions = isTeacher
    ? [
        { key: 'workspace', label: 'Workspace', tabKeys: ['performance', 'settings', 'subUsers', 'sessions'] },
        { key: 'instruction', label:'Instruction',tabKeys:['lessonMonitor'] },
        { key: 'assessmentTools', label: 'Assessment Tools', tabKeys: ['reports', 'createAssessment'] },
      ]
    : [
        { key: 'peopleSetup', label: 'People & Setup', tabKeys: ['assign', 'userList', 'settings', 'assessments'] },
        { key: 'assessmentActivity', label: 'Assessment Activity', tabKeys: ['performance', 'responses', 'sessions', 'reports'] },
        { key: 'instruction', label:'Instruction',tabKeys:['lessonMonitor'] },
        { key: 'insightsOperations', label: 'Insights & Operations', tabKeys: ['questionHistory', 'itemAnalysis', 'itemBank', 'operationalHealth'] },
      ];

  const tabByKey = new Map(tabsToShow.map(tab => [tab.key, tab]));
  const tabIndexByKey = new Map(tabsToShow.map((tab, index) => [tab.key, index]));
  const menuGroups = menuGroupDefinitions
    .map(group => ({
      ...group,
      tabs: group.tabKeys.map(key => tabByKey.get(key)).filter(Boolean),
    }))
    .filter(group => group.tabs.length > 0);
  const activeTabLabel = tabsToShow.find(tab => tab.key === activeTab)?.label || 'Dashboard';

  /* ------------------------------------------------------------------
     Enhanced CSS for hover / animations and responsive tabs
  ------------------------------------------------------------------*/
  useEffect(() => {
    if (!document.querySelector('#admin-dashboard-styles')) {
      const s = document.createElement('style');
      s.id = 'admin-dashboard-styles';
      s.textContent = dashboardCss(`
        @keyframes fadeIn   { 0%{opacity:0;transform:translateY(10px)} 100%{opacity:1;transform:translateY(0)} }
        @keyframes slideDown{ 0%{transform:translateY(-100%);opacity:0} 100%{transform:translateY(0);opacity:1} }
        @keyframes pulse    { 0%{transform:scale(1)} 50%{transform:scale(1.02)} 100%{transform:scale(1)} }
        
        .tab-button:hover {
          border-color: rgba(94,234,212,.48) !important;
          background-image: linear-gradient(135deg,rgba(51,65,85,.96),rgba(30,41,59,.96)) !important;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(2,6,23,.28), 0 0 0 1px rgba(16,185,129,.16) !important;
        }
        
        .tab-button:active {
          transform: translateY(0) scale(0.99);
        }
        
        .tab-button.active {
          animation: pulse 0.3s ease-out;
        }
        
        .refresh-button:hover { 
          background-image: linear-gradient(to right,#059669,#047857) !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 4px 10px rgba(13,148,136,.3) !important; 
        }
        
        .logout-button:hover { 
          background-color: #b91c1c !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 4px 6px rgba(239,68,68,.3) !important; 
        }

        /* Centered dashboard navigation deck */
        .tabs-container {
          width: min(100%, 1560px);
          margin: 0 auto 1.5rem;
          padding: 1rem;
          background: linear-gradient(145deg,rgba(30,41,59,.88),rgba(15,23,42,.78));
          backdrop-filter: blur(18px);
          border-radius: 1.25rem;
          border: 1px solid rgba(148,163,184,.22);
          box-shadow: 0 18px 45px rgba(2,6,23,.24), inset 0 1px 0 rgba(255,255,255,.035);
          overflow: visible;
        }

        .dashboard-menu-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
          padding: 0 .25rem .8rem;
          color: #cbd5e1;
          font-size: .8rem;
          font-weight: 700;
          letter-spacing: .08em;
          text-transform: uppercase;
        }

        .dashboard-menu-title,
        .dashboard-menu-current {
          display: inline-flex;
          align-items: center;
          gap: .45rem;
        }

        .dashboard-menu-current {
          padding: .35rem .65rem;
          color: #99f6e4;
          background: rgba(16,185,129,.1);
          border: 1px solid rgba(45,212,191,.2);
          border-radius: 999px;
          letter-spacing: .025em;
          text-transform: none;
        }

        .dashboard-menu-groups {
          display: grid;
          grid-template-columns: repeat(3,minmax(0,1fr));
          justify-content: center;
          gap: .8rem;
        }

        .dashboard-menu-group {
          min-width: 0;
          padding: .7rem;
          background: rgba(15,23,42,.48);
          border: 1px solid rgba(148,163,184,.14);
          border-radius: 1rem;
        }

        .dashboard-menu-group-label {
          display: flex;
          align-items: center;
          gap: .45rem;
          margin: 0 .2rem .55rem;
          color: #94a3b8;
          font-size: .72rem;
          font-weight: 800;
          letter-spacing: .075em;
          text-transform: uppercase;
        }

        .dashboard-menu-group-label::before {
          content: '';
          width: .45rem;
          height: .45rem;
          border-radius: 999px;
          background: linear-gradient(135deg,#34d399,#2dd4bf);
          box-shadow: 0 0 10px rgba(45,212,191,.45);
        }

        .dashboard-menu-group-tabs {
          display: grid;
          grid-template-columns: repeat(2,minmax(0,1fr));
          gap: .5rem;
        }

        .tab-button {
          width: 100%;
          min-width: 0;
          min-height: 46px;
        }

        .tab-button.active:hover {
          background-image: linear-gradient(135deg,#10b981,#0d9488) !important;
          border-color: rgba(110,231,183,.72) !important;
        }

        .tab-button:focus-visible {
          outline: 3px solid rgba(94,234,212,.7) !important;
          outline-offset: 2px;
        }

        .tab-active-indicator {
          position: absolute;
          left: .45rem;
          width: 3px;
          height: 1.35rem;
          border-radius: 999px;
          background: #ecfdf5;
          box-shadow: 0 0 9px rgba(236,253,245,.65);
        }

        @media (max-width: 1450px) {
          .dashboard-menu-groups {
            grid-template-columns: repeat(2,minmax(320px,1fr));
          }

          .dashboard-menu-group:last-child:nth-child(odd) {
            grid-column: 1 / -1;
            width: calc(50% - .4rem);
            justify-self: center;
          }
        }

        @media (max-width: 1023px) {
          .tabs-container {
            padding: .8rem;
          }

          .dashboard-menu-groups {
            grid-template-columns: minmax(0,1fr);
          }

          .dashboard-menu-group:last-child:nth-child(odd) {
            grid-column: auto;
            width: 100%;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .tab-button,
          .tab-button.active {
            animation: none !important;
            transition: none !important;
          }
        }
      `);
      document.head.appendChild(s);
    }
  }, []);

  /* responsive */
  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 768;
      const tablet = window.innerWidth >= 768 && window.innerWidth < 1024;
      setIsMobileView(mobile);
      setIsTabletView(tablet);
      if (!mobile) setIsMobileMenuOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isMobileMenuOpen]);

  /* ------------------------------------------------------------------
     handlers
  ------------------------------------------------------------------*/
  const handleLogoutClick = async () => {
    if (logoutInFlight.current) return;
    logoutInFlight.current = true;

    const serverLogout = api.logout();
    logoutUser();
    onLogout?.();
    try {
      await serverLogout;
    } catch (error) {
      // Local and React state are still cleared by api.logout/onLogout so a
      // shared device cannot retain the outgoing user's browser session.
      console.warn('Server logout could not be confirmed:', error);
    }
  };

  const handleTabClick = key => {
    setActiveTab(key);
    if (isMobileView) setIsMobileMenuOpen(false);
  };

  const handleTabKeyDown = (event, currentIndex) => {
    const lastIndex = tabsToShow.length - 1;
    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = lastIndex;
    } else {
      return;
    }

    event.preventDefault();
    const nextTab = tabsToShow[nextIndex];
    if (!nextTab) return;
    handleTabClick(nextTab.key);
    window.requestAnimationFrame(() => desktopTabRefs.current[nextIndex]?.focus());
  };

  const handleRefreshClick = async () => {
    setIsRefreshing(true);
    setRefreshError(null);
    try {
      const jobs = [
        refreshUsers(),
        ...assignedUsers.map(u => forceRefreshUserSettings(u.id, true)),
        getPerformanceStats?.(),
        refreshSessions(),
      ];

      if (!isTeacher) {
        jobs.push(
          refreshAssessments(),
          refreshStudentResponses(),
          assessmentSettings ? Promise.resolve() : Promise.reject('Assessment settings not available'),
        );
      }

      const results = await Promise.allSettled(jobs);
      const failed = results.filter(r => r.status === 'rejected');
      if (failed.length) {
        const msg = failed.map(f => f.reason).join('; ');
        throw new Error(msg);
      }
      alert('All data refreshed successfully!');
    } catch (err) {
      console.error('Refresh error:', err);
      setRefreshError(err instanceof Error ? err.message : 'Failed to refresh all data. Please try again.');
      alert(`Refresh failed: ${err}`);
    } finally {
      setIsRefreshing(false);
    }
  };

  /* helper */
  const getAccessCode = () =>
    currentUser?.accessCode || currentUser?.access_code || '';

  /* ------------------------------------------------------------------
     Enhanced responsive styles
  ------------------------------------------------------------------*/
  const containerStyle = {
    minHeight: '100vh',
    background: dashboardPaint('linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)', 'background', false),
    fontFamily: "'Segoe UI',Tahoma,Geneva,Verdana,sans-serif",
    color: dashboardPaint('#e2e8f0', 'text', false),
    position: 'relative',
    overflow: 'hidden',
  };

  const blobContainerStyle = {
    position: 'absolute',
    inset: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
    zIndex: 0,
  };

  const navBarStyle = {
    backgroundColor: dashboardPaint('rgba(15,23,42,.9)', 'background', false),
    backdropFilter: 'blur(16px)',
    boxShadow: dashboardPaint('0 4px 20px rgba(0,0,0,.3)', 'shadow', false),
    padding: isMobileView ? '0.75rem 0' : '1rem 0',
    position: 'sticky',
    top: 0,
    zIndex: 50,
    borderBottom: dashboardPaint('1px solid rgba(148,163,184,.3)', 'border', false),
    animation: 'slideDown .3s ease-out',
  };

  const navInnerStyle = {
    maxWidth: 1600,
    margin: '0 auto',
    padding: isMobileView ? '0 1rem' : '0 2rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  };

  const navTitleStyle = {
    fontSize: isMobileView ? '1.25rem' : '1.75rem',
    fontWeight: 'bold',
    background: dashboardPaint('linear-gradient(135deg, #10b981, #0d9488)', 'background', true), '--dashboard-action-foreground': dashboardActionForeground('linear-gradient(135deg, #10b981, #0d9488)'),
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  };

  const mobileMenuButtonStyle = {
    background: dashboardPaint('none', 'background', true), '--dashboard-action-foreground': dashboardActionForeground('none'),
    border: dashboardPaint('none', 'border', true),
    color: dashboardPaint('#10b981', 'text', true),
    fontSize: '1.5rem',
    cursor: 'pointer',
    padding: '0.25rem',
    marginRight: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '0.5rem',
    transition: 'all 0.2s ease',
  };

  const navButtonsContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: isMobileView ? '0.5rem' : '1rem',
  };

  const buttonBaseStyle = {
    border: dashboardPaint('none', 'border', true),
    borderRadius: '0.5rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all .2s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.25rem',
    outline: dashboardPaint('none', 'border', true),
    position: 'relative',
  };

  const refreshBtnStyle = {
    ...buttonBaseStyle,
    padding: isMobileView ? '0.5rem 0.75rem' : '0.75rem 1.5rem',
    backgroundImage: dashboardPaint('linear-gradient(135deg, #10b981, #0d9488)', 'background', true), '--dashboard-action-foreground': dashboardActionForeground('linear-gradient(135deg, #10b981, #0d9488)'),
    color: dashboardPaint('#fff', 'text', true),
    fontSize: isMobileView ? '0.875rem' : '1rem',
    boxShadow: dashboardPaint('0 4px 15px rgba(16,185,129,.25)', 'shadow', true),
  };

  const logoutBtnStyle = {
    ...buttonBaseStyle,
    padding: isMobileView ? '0.5rem 0.75rem' : '0.75rem 1.5rem',
    backgroundColor: dashboardPaint('#ef4444', 'background', true), '--dashboard-action-foreground': dashboardActionForeground('#ef4444'),
    color: dashboardPaint('#fff', 'text', true),
    fontSize: isMobileView ? '0.875rem' : '1rem',
    boxShadow: dashboardPaint('0 4px 15px rgba(239,68,68,.25)', 'shadow', true),
  };

  const mainContainerStyle = {
    maxWidth: 1800,
    margin: isMobileView ? '1rem auto' : '2rem auto',
    padding: isMobileView ? '0.5rem' : '1rem',
    position: 'relative',
    zIndex: 1,
  };

  const mobileMenuStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: dashboardPaint('rgba(15,23,42,.98)', 'background', false),
    backdropFilter: 'blur(20px)',
    zIndex: 100,
    display: isMobileMenuOpen ? 'flex' : 'none',
    flexDirection: 'column',
    padding: '1rem',
    animation: 'fadeIn .3s ease-out',
  };

  const mobileMenuCloseStyle = {
    alignSelf: 'flex-end',
    background: dashboardPaint('none', 'background', false),
    border: dashboardPaint('none', 'border', false),
    color: dashboardPaint('#e2e8f0', 'text', false),
    fontSize: '1.5rem',
    padding: '0.5rem',
    cursor: 'pointer',
    borderRadius: '0.5rem',
    transition: 'all 0.2s ease',
  };

  const mobileTabsContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    marginTop: '2rem',
    alignItems: 'center',
  };

  const mobileTabButtonStyle = isActive => ({
    padding: '0.75rem 1rem',
    width: '80%',
    borderRadius: '0.75rem',
    border: dashboardPaint('none', 'border', true),
    backgroundColor: dashboardPaint(isActive ? 'rgba(16,185,129,.9)' : 'rgba(51,65,85,.8)', 'background', true), '--dashboard-action-foreground': dashboardActionForeground(isActive ? 'rgba(16,185,129,.9)' : 'rgba(51,65,85,.8)'),
    color: dashboardPaint(isActive ? '#fff' : '#e2e8f0', 'text', true),
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '0.75rem',
    backdropFilter: 'blur(8px)',
    transition: 'all .2s ease',
    boxShadow: dashboardPaint(isActive
      ? '0 6px 20px rgba(16,185,129,.3)'
      : '0 4px 10px rgba(0,0,0,.2)', 'shadow', true),
  });

  // Enhanced responsive tab button styling
  const getTabButtonStyle = (isActive) => {
    const baseSize = isMobileView ? 'small' : isTabletView ? 'medium' : 'large';
    
    const sizeConfig = {
      small: { padding: '0.5rem 0.75rem', fontSize: '0.75rem', iconSize: 14 },
      medium: { padding: '0.6rem 1rem', fontSize: '0.875rem', iconSize: 15 },
      large: { padding: '0.75rem 1.25rem', fontSize: '0.95rem', iconSize: 16 }
    };

    const config = sizeConfig[baseSize];
    
    return {
      padding: config.padding,
      borderRadius: '0.75rem',
      border: dashboardPaint(isActive
        ? '1px solid rgba(110,231,183,.6)'
        : '1px solid rgba(148,163,184,.18)', 'border', true),
      backgroundImage: dashboardPaint(isActive
        ? 'linear-gradient(135deg,#10b981,#0d9488)'
        : 'linear-gradient(135deg,rgba(51,65,85,.84),rgba(30,41,59,.84))', 'background', true), '--dashboard-action-foreground': dashboardActionForeground(isActive
        ? 'linear-gradient(135deg,#10b981,#0d9488)'
        : 'linear-gradient(135deg,rgba(51,65,85,.84),rgba(30,41,59,.84))'),
      color: dashboardPaint(isActive ? '#fff' : '#e2e8f0', 'text', true),
      fontSize: config.fontSize,
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all .25s cubic-bezier(0.4, 0, 0.2, 1)',
      outline: dashboardPaint('none', 'border', true),
      whiteSpace: 'nowrap',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: '0.4rem',
      backdropFilter: 'blur(8px)',
      boxShadow: dashboardPaint(isActive
        ? '0 6px 20px rgba(16,185,129,.3), 0 0 0 1px rgba(16,185,129,.5)'
        : '0 2px 8px rgba(0,0,0,.1), 0 0 0 1px rgba(148,163,184,.2)', 'shadow', true),
      position: 'relative',
      overflow: 'hidden',
      width: '100%',
      minWidth: 0,
      minHeight: '46px',
      paddingLeft: isActive ? '1rem' : config.padding.split(' ')[1] || '1rem',
    };
  };

  const contentAreaStyle = {
    backgroundColor: dashboardPaint('rgba(15,23,42,.9)', 'background', false),
    backdropFilter: 'blur(16px)',
    borderRadius: '1.25rem',
    boxShadow: dashboardPaint('0 10px 25px rgba(0,0,0,.3), 0 0 0 1px rgba(148,163,184,.1)', 'shadow', false),
    padding: isMobileView ? '1rem' : '2rem',
    overflowX: 'hidden',
    border: dashboardPaint('1px solid rgba(148,163,184,.2)', 'border', false),
    animation: 'fadeIn .4s ease',
  };

  const refreshErrorStyle = {
    backgroundColor: dashboardPaint('rgba(153,27,27,.3)', 'background', false),
    color: dashboardPaint('#fecaca', 'text', false),
    padding: '0.75rem',
    borderRadius: '0.75rem',
    marginLeft: isMobileView ? 0 : '1rem',
    marginTop: isMobileView ? '0.5rem' : 0,
    fontSize: '0.875rem',
    width: isMobileView ? '100%' : 'auto',
    border: dashboardPaint('1px solid rgba(220,38,38,.3)', 'border', false),
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  };

  const debugInfoStyle = {
    backgroundColor: dashboardPaint('rgba(6,78,59,.3)', 'background', false),
    padding: '0.75rem',
    marginBottom: '1rem',
    borderRadius: '0.75rem',
    fontSize: '0.875rem',
    border: dashboardPaint('1px solid rgba(16,185,129,.3)', 'border', false),
    color: dashboardPaint('#a7f3d0', 'text', false),
  };

  /* helper function to get appropriate label */
  const getTabLabel = (tab) => {
    if (isMobileView) return tab.shortLabel || tab.label;
    if (isTabletView && tab.shortLabel && tab.label.length > 8) return tab.shortLabel;
    return tab.label;
  };

  /* ------------------------------------------------------------------
     render
  ------------------------------------------------------------------*/
  return (
    <div className="educator-dashboard" style={containerStyle}>
      {/* enhanced decorative blobs */}
      <div style={blobContainerStyle}>
        <div style={{
          position: 'absolute', top: '10%', left: '20%', width: '35rem', height: '35rem',
          background: dashboardPaint('radial-gradient(circle, rgba(16,185,129,.08) 0%, rgba(16,185,129,.02) 70%)', 'background', true), '--dashboard-action-foreground': dashboardActionForeground('radial-gradient(circle, rgba(16,185,129,.08) 0%, rgba(16,185,129,.02) 70%)'),
          borderRadius: '50%', filter: 'blur(120px)', animation: 'pulse 8s infinite',
        }} />
        <div style={{
          position: 'absolute', bottom: '15%', right: '20%', width: '30rem', height: '30rem',
          background: dashboardPaint('radial-gradient(circle, rgba(13,148,136,.06) 0%, rgba(13,148,136,.01) 70%)', 'background', false),
          borderRadius: '50%', filter: 'blur(100px)', animation: 'pulse 10s infinite reverse',
        }} />
        <div style={{
          position: 'absolute', top: '40%', right: '10%', width: '25rem', height: '25rem',
          background: dashboardPaint('radial-gradient(circle, rgba(139,92,246,.05) 0%, rgba(139,92,246,.01) 70%)', 'background', false),
          borderRadius: '50%', filter: 'blur(90px)', animation: 'pulse 12s infinite',
        }} />
      </div>

      {/* Mobile Menu */}
      {isMobileView && (
        <div style={mobileMenuStyle}>
          <button style={mobileMenuCloseStyle} onClick={() => setIsMobileMenuOpen(false)}>
            <X size={24} />
          </button>
          <div style={mobileTabsContainerStyle}>
            {tabsToShow.map(tab => (
              <button
                key={tab.key}
                style={mobileTabButtonStyle(activeTab === tab.key)}
                onClick={() => handleTabClick(tab.key)}
                onPointerDown={() => preloadDashboardTab(tab.key, isTeacher)}
                onFocus={() => preloadDashboardTab(tab.key, isTeacher)}
              >
                {tab.icon} {tab.label}
              </button>
            ))}

            {/* refresh (mobile) – admin only */}
            {!isTeacher && (
              <button
                onClick={handleRefreshClick}
                style={{
                  ...mobileTabButtonStyle(false),
                  backgroundImage: dashboardPaint('linear-gradient(135deg, #10b981, #0d9488)', 'background', true), '--dashboard-action-foreground': dashboardActionForeground('linear-gradient(135deg, #10b981, #0d9488)'),
                  color: dashboardPaint('#fff', 'text', true),
                  opacity: isRefreshing ? 0.5 : 1,
                  cursor: isRefreshing ? 'not-allowed' : 'pointer',
                  marginTop: '1rem',
                }}
                disabled={isRefreshing}
              >
                <RefreshCw size={18} />
                {isRefreshing ? 'Refreshing…' : 'Refresh Data'}
              </button>
            )}

            <button
              onClick={handleLogoutClick}
              style={{
                ...mobileTabButtonStyle(false),
                backgroundColor: dashboardPaint('#ef4444', 'background', true), '--dashboard-action-foreground': dashboardActionForeground('#ef4444'),
                color: dashboardPaint('#fff', 'text', true),
              }}
            >
              <LogOut size={18} /> Logout
            </button>
          </div>
        </div>
      )}

      {/* NavBar */}
      <nav style={navBarStyle}>
        <div style={navInnerStyle}>
          <h1 style={navTitleStyle}>
            {isMobileView && (
              <button
                style={mobileMenuButtonStyle}
                onClick={() => setIsMobileMenuOpen(true)}
                aria-label="Open menu"
              >
                <Menu size={24} />
              </button>
            )}
            <Shield size={24} /> Dashboard {!isMobileView && `(${isTeacher ? 'Teacher' : 'Admin'})`}
          </h1>

          <DashboardThemeControl />
          {!isMobileView && (
            <div style={navButtonsContainerStyle}>
              {/* refresh (desktop) – admin only */}
              {!isTeacher && (
                <button
                  onClick={handleRefreshClick}
                  style={{
                    ...refreshBtnStyle,
                    opacity: isRefreshing ? 0.5 : 1,
                    cursor: isRefreshing ? 'not-allowed' : 'pointer',
                  }}
                  disabled={isRefreshing}
                  className="refresh-button"
                >
                  <RefreshCw size={18} />
                  {isRefreshing ? 'Refreshing…' : 'Refresh'}
                </button>
              )}

              <button
                onClick={handleLogoutClick}
                style={logoutBtnStyle}
                className="logout-button"
              >
                <LogOut size={18} /> Logout
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Error banner */}
      {refreshError && (
        <div style={{ ...refreshErrorStyle, margin: '1rem auto', maxWidth: 800 }}>
          <AlertTriangle size={18} color={dashboardPaint("#fecaca", 'text')} />
          {refreshError}
        </div>
      )}

      {/* Content wrapper */}
      <div style={mainContainerStyle}>
        {/* Enhanced responsive tabs (desktop/tablet) */}
        {!isMobileView && (
          <div className="tabs-container" role="tablist" aria-label="Dashboard sections">
            <div className="dashboard-menu-header" role="presentation">
              <span className="dashboard-menu-title"><Menu size={16} /> Workspace menu</span>
              <span className="dashboard-menu-current">Current: {activeTabLabel}</span>
            </div>
            <div className="dashboard-menu-groups" role="presentation">
              {menuGroups.map(group => (
                <div className="dashboard-menu-group" role="presentation" key={group.key}>
                  <div className="dashboard-menu-group-label" role="presentation">{group.label}</div>
                  <div className="dashboard-menu-group-tabs" role="presentation">
                    {group.tabs.map(tab => {
                      const tabIndex = tabIndexByKey.get(tab.key);
                      const isActive = activeTab === tab.key;
                      return (
                        <button
                          key={tab.key}
                          ref={element => { desktopTabRefs.current[tabIndex] = element; }}
                          id={`dashboard-tab-${tab.key}`}
                          style={getTabButtonStyle(isActive)}
                          onClick={() => handleTabClick(tab.key)}
                          onKeyDown={event => handleTabKeyDown(event, tabIndex)}
                          onPointerEnter={() => preloadDashboardTab(tab.key, isTeacher)}
                          onFocus={() => preloadDashboardTab(tab.key, isTeacher)}
                          className={`tab-button ${isActive ? 'active' : ''}`}
                          role="tab"
                          aria-selected={isActive}
                          aria-controls={`dashboard-panel-${tab.key}`}
                          tabIndex={isActive ? 0 : -1}
                        >
                          {isActive && <span className="tab-active-indicator" aria-hidden="true" />}
                          {React.cloneElement(tab.icon, {
                            size: isTabletView ? 15 : 16,
                            'aria-hidden': true,
                          })}
                          <span>{getTabLabel(tab)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* main content */}
        <div
          style={contentAreaStyle}
          role="tabpanel"
          id={`dashboard-panel-${activeTab}`}
          aria-labelledby={!isMobileView ? `dashboard-tab-${activeTab}` : undefined}
          tabIndex={0}
        >
          {activeTab === 'settings' && <section style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 14, padding: 16, marginBottom: 18, border: dashboardPaint('1px solid #64748b', 'border'), borderRadius: 10 }} aria-label="Dashboard appearance settings">
            <div><strong>Dashboard appearance</strong><div style={{ marginTop: 4, fontSize: 13, color: dashboardPaint('#94a3b8') }}>Choose your preferred educator workspace. Student views and printed pages use their own layouts.</div></div>
            <DashboardThemeControl />
          </section>}
          <Suspense fallback={<div role="status" aria-live="polite">Loading dashboard section…</div>}>
            {activeTab === 'assign'           && !isTeacher && <AssignUser />}
            {activeTab === 'userList'         && !isTeacher && <UserList />}
            {activeTab === 'subUsers'                         && <SubUserList />}
            {activeTab === 'settings'         && (
                isTeacher
                  ? <TeacherAssessmentSettings />   /* teacher-specific view */
                  : <AssessmentSettings />          /* original admin view  */
              )}
            {activeTab === 'performance'                      && <StudentPerformance />}
            {activeTab === 'assessments'      && !isTeacher && <AssessmentManager />}
            {activeTab === 'createAssessment' && isTeacher && subUsers.length > 0 && <TeacherAssessmentManager />}
            {activeTab === 'responses'        && !isTeacher && <StudentResponses />}
            {activeTab === 'reports'                          && <Reports />}
            {activeTab === 'sessions' && (
              <>
                {isTeacher && (
                  <div style={debugInfoStyle}>
                    Debug: Filtering sessions by access code: {getAccessCode()}
                  </div>
                )}
                <SessionManagement filterAccessCode={isTeacher ? getAccessCode() : ''} />
              </>
            )}
            {activeTab==='lessonMonitor'&&<LessonMonitor initialLearnerId={monitorLearner}/>}
            {/* Question History - Admin Only */}
            {activeTab === 'questionHistory'  && !isTeacher && (
              <QuestionHistoryAdmin accessCode={getAccessCode()} />
            )}
            {/* Item Analysis - Admin Only */}
            {activeTab === 'itemAnalysis'     && !isTeacher && (
              <ItemAnalysis />
            )}
            {activeTab === 'itemBank'         && !isTeacher && (
              <ItemBank />
            )}
            {activeTab === 'operationalHealth' && !isTeacher && (
              <OperationalHealth />
            )}
          </Suspense>
        </div>
        <AppFooter compact style={{ borderTop: dashboardPaint('1px solid rgba(148, 163, 184, 0.18)', 'border', false), marginTop: '1rem' }} />
      </div>
    </div>
  );
}

export default function AdminDashboard(props) {
  const { currentUser } = useAdmin();
  const accountKey = currentUser?.role === 'admin' ? 'admin' : `user:${currentUser?.id}`;
  return <DashboardThemeProvider key={accountKey} user={currentUser}><DashboardContent {...props} /></DashboardThemeProvider>;
}
